'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useIsHydrated } from '@/hooks/useIsHydrated';
import Icon from '@/components/ui/AppIcon';
import PhoneField from '@/components/ui/PhoneField';
import { sendLead, lastLeadError } from '@/lib/sendLead';
import { subscribeNewsletter } from '@/lib/subscribeNewsletter';
import { track } from '@/lib/analytics';
import { isValidEmail, isValidPhone, toE164 } from '@/lib/validation';
import { DEFAULT_COUNTRY } from '@/lib/countries';
import { CONTACT } from '@/lib/contact';

const MAX_MESSAGE = 800;

interface FormData {
  fullName: string;
  email: string;
  company: string;
  dialCode: string;
  phone: string;
  message: string;
  consent: boolean;
  newsletter: boolean;
}

interface FormErrors {
  [key: string]: string;
}

interface ProductInquiryFormProps {
  /** 'quote' relabels the form and files the lead as a quote request. */
  intent?: 'contact' | 'quote';
}

// Deep-link prefill: ?product=<name> seeds the message with context. Read from
// window.location (not useSearchParams, which would force this static page into
// a dynamic/Suspense render); computed once as the initial state.
function readInitialFormData(): FormData {
  const base: FormData = {
    fullName: '',
    email: '',
    company: '',
    dialCode: DEFAULT_COUNTRY.dialCode,
    phone: '',
    message: '',
    consent: true,
    newsletter: true,
  };
  if (typeof window === 'undefined') return base;
  const product = new URLSearchParams(window.location.search).get('product');
  if (product) base.message = `I'd like to enquire about: ${product}\n\n`;
  return base;
}

const fieldClass =
  'w-full px-4 py-3 bg-muted border border-border rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors';

const ProductInquiryForm: React.FC<ProductInquiryFormProps> = ({ intent = 'contact' }) => {
  const isHydrated = useIsHydrated();
  const isQuote = intent === 'quote';
  const [formData, setFormData] = useState<FormData>(readInitialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Honeypot: real users leave this empty.
  const [website, setWebsite] = useState('');

  const validateField = (name: keyof FormData, data: FormData): string => {
    switch (name) {
      case 'fullName':
        return data.fullName.trim() ? '' : 'Full name is required';
      case 'email':
        return isValidEmail(data.email) ? '' : 'A valid email is required';
      case 'phone':
        return isValidPhone(data.phone) ? '' : 'A valid phone number is required';
      case 'message':
        return data.message.trim() ? '' : 'Please add a short message';
      case 'consent':
        return data.consent ? '' : 'Please agree to be contacted so we can respond';
      default:
        return '';
    }
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    (['fullName', 'email', 'phone', 'message', 'consent'] as const).forEach((f) => {
      const msg = validateField(f, formData);
      if (msg) next[f] = msg;
    });
    setErrors(next);
    if (Object.keys(next).length > 0) {
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      });
      return false;
    }
    return true;
  };

  const handleBlur = (name: keyof FormData) => {
    setErrors((prev) => ({ ...prev, [name]: validateField(name, formData) }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError('');

    const result = await sendLead({
      type: intent,
      honeypot: website,
      subject: `${isQuote ? 'Quote request' : 'General enquiry'}  - ${
        formData.company || formData.fullName
      }`,
      fields: {
        Name: formData.fullName,
        Email: formData.email,
        Company: formData.company,
        Phone: toE164(formData.dialCode, formData.phone),
        Message: formData.message,
        Consent: formData.consent ? 'Yes' : 'No',
      },
    });

    if (result === 'invalid') {
      setSubmitError(lastLeadError || 'Please check the details you entered.');
      setIsSubmitting(false);
      return;
    }
    if (result === 'rate-limited') {
      setSubmitError('Too many submissions in a short time. Please try again in a minute.');
      setIsSubmitting(false);
      return;
    }
    if (result === 'error') {
      setSubmitError('Something went wrong sending your message. Please try again or email us.');
      setIsSubmitting(false);
      return;
    }

    if (formData.newsletter) {
      void subscribeNewsletter(formData.email.trim());
      track('newsletter_signup', { source: isQuote ? 'quote_form' : 'contact_form' });
    }
    track('generate_lead', { form: intent });
    setSubmitSuccess(true);
    setIsSubmitting(false);
  };

  if (!isHydrated) {
    return (
      <div
        className="bg-surface border border-border rounded-2xl p-6 md:p-8 min-h-112 animate-pulse"
        aria-hidden="true"
      />
    );
  }

  if (submitSuccess) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center"
      >
        <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} className="text-success" />
        </div>
        <h2 className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-3">
          Thank you!
        </h2>
        <p className="font-body text-lg text-muted-foreground max-w-md mx-auto">
          Your {isQuote ? 'quote request' : 'message'} is on its way to our team.{' '}
          {/* {CONTACT.responsePromise}. */}
        </p>
      </div>
    );
  }

  const messageOver = formData.message.length > MAX_MESSAGE;

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 md:p-8">
      <h2 className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-2">
        {isQuote ? 'Request a Quote' : 'Send us a message'}
      </h2>
      <p className="font-body text-muted-foreground mb-6">
        {isQuote
          ? 'Tell us what you need and we’ll send tailored pricing.'
          : 'Questions about our compostable foodware or wholesale? We’d love to hear from you.'}{' '}
        {/* {CONTACT.responsePromise}. */}
      </p>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* Honeypot: hidden from real users, catches bots. */}
        <div aria-hidden="true" className="absolute w-px h-px overflow-hidden -left-[9999px]">
          <label htmlFor="website-hp">Website</label>
          <input
            id="website-hp"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label
              htmlFor="fullName"
              className="block font-cta text-sm font-medium text-foreground mb-2"
            >
              Full Name <span className="text-accent">*</span>
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              autoComplete="name"
              value={formData.fullName}
              onChange={handleChange}
              onBlur={() => handleBlur('fullName')}
              aria-required="true"
              aria-invalid={!!errors.fullName}
              aria-describedby={errors.fullName ? 'fullName-error' : undefined}
              className={fieldClass}
              placeholder="John Doe"
            />
            {errors.fullName && (
              <p id="fullName-error" role="alert" className="text-error text-sm mt-1">
                {errors.fullName}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="email"
              className="block font-cta text-sm font-medium text-foreground mb-2"
            >
              Email <span className="text-accent">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => handleBlur('email')}
              aria-required="true"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              className={fieldClass}
              placeholder="john@company.com"
            />
            {errors.email && (
              <p id="email-error" role="alert" className="text-error text-sm mt-1">
                {errors.email}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="company"
              className="block font-cta text-sm font-medium text-foreground mb-2"
            >
              Company
            </label>
            <input
              id="company"
              name="company"
              type="text"
              autoComplete="organization"
              value={formData.company}
              onChange={handleChange}
              className={fieldClass}
              placeholder="Your Company"
            />
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block font-cta text-sm font-medium text-foreground mb-2"
            >
              Phone <span className="text-accent">*</span>
            </label>
            <PhoneField
              id="phone"
              fieldClassName="bg-muted rounded-xl"
              dialCode={formData.dialCode}
              onDialCodeChange={(dialCode) => setFormData((prev) => ({ ...prev, dialCode }))}
              value={formData.phone}
              onChange={(phone) => {
                setFormData((prev) => ({ ...prev, phone }));
                setErrors((prev) => (prev.phone ? { ...prev, phone: '' } : prev));
              }}
              onBlur={() => handleBlur('phone')}
              error={errors.phone}
              errorId="phone-error"
              required
            />
            {errors.phone && (
              <p id="phone-error" role="alert" className="text-error text-sm mt-1">
                {errors.phone}
              </p>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="message" className="block font-cta text-sm font-medium text-foreground">
              Message <span className="text-accent">*</span>
            </label>
            <span
              aria-live="polite"
              className={`font-body text-xs ${messageOver ? 'text-error' : 'text-muted-foreground'}`}
            >
              {formData.message.length}/{MAX_MESSAGE}
            </span>
          </div>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={() => handleBlur('message')}
            rows={3}
            maxLength={MAX_MESSAGE}
            aria-required="true"
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? 'message-error' : undefined}
            className={`${fieldClass} resize-y`}
            placeholder="How can we help?"
          />
          {errors.message && (
            <p id="message-error" role="alert" className="text-error text-sm mt-1">
              {errors.message}
            </p>
          )}
        </div>

        <div className="flex items-start gap-3">
          <input
            id="consent"
            type="checkbox"
            name="consent"
            checked={formData.consent}
            onChange={(e) => {
              const consent = e.target.checked;
              setFormData((prev) => ({ ...prev, consent }));
              if (consent) setErrors((prev) => ({ ...prev, consent: '' }));
            }}
            aria-required="true"
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? 'consent-error' : undefined}
            className="mt-1 w-4 h-4 text-primary bg-muted border-border rounded focus:ring-primary"
          />
          <label htmlFor="consent" className="font-body text-sm text-muted-foreground">
            I agree to be contacted regarding my enquiry, in line with the{' '}
            <Link href="/legal/privacy" className="text-info underline hover:text-info/80">
              Privacy Policy
            </Link>
            . <span className="text-accent">*</span>
          </label>
        </div>
        {errors.consent && (
          <p id="consent-error" role="alert" className="text-error text-sm -mt-2">
            {errors.consent}
          </p>
        )}

        <div className="flex items-start gap-3">
          <input
            id="newsletter"
            type="checkbox"
            name="newsletter"
            checked={formData.newsletter}
            onChange={(e) => setFormData((prev) => ({ ...prev, newsletter: e.target.checked }))}
            className="mt-1 w-4 h-4 text-primary bg-muted border-border rounded focus:ring-primary"
          />
          <label htmlFor="newsletter" className="font-body text-sm text-muted-foreground">
            Subscribe for sustainability tips and product updates (monthly). You can unsubscribe any
            time.
          </label>
        </div>

        {submitError && (
          <p role="alert" className="text-error text-sm">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-primary text-primary-foreground font-cta font-semibold rounded-xl hover:bg-primary/90 transition active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50 disabled:active:scale-100"
        >
          {isSubmitting ? 'Sending…' : isQuote ? 'Request Quote' : 'Send Message'}
          {!isSubmitting && <Icon name="PaperAirplaneIcon" size={18} aria-hidden="true" />}
        </button>
      </form>
    </div>
  );
};

export default ProductInquiryForm;
